import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, setBlockTracking as _setBlockTracking, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode } from "vue";
const _hoisted_1 = { class: "card" };
const _hoisted_2 = {
    key: 0,
    class: "px-3 lg:px-4 pt-3 pb-2"
};
const _hoisted_3 = { class: "flex justify-between items-center" };
const _hoisted_4 = { class: "font-semibold" };
const _hoisted_5 = ["aria-label", "title"];
const _hoisted_6 = { class: "my-1 text-muted collapsible-content" };
const _hoisted_7 = { class: "w-full relative aspect-2/1 md:aspect-video lg:aspect-2/1" };
const _hoisted_8 = {
    key: 0,
    class: "absolute inset-0 flex justify-center items-center text-muted z-2"
};
const _hoisted_9 = {
    key: 1,
    class: "px-3 lg:px-4 pt-1 pb-3 lg:pb-4"
};
import { ref, watch, computed } from 'vue';
import { ___, toFixedNumberStr } from '@ts/common/language';
import { useDeferredChart } from '@ts/common/useDeferredChart';
import { closest } from '@ts/common/math';
import LabeledSelect from '@ts/components/form/inputs/LabeledSelect.vue';
import {} from '@ts/components/form/types';
import {} from '@ts/common/datasets';
import SvgVue from '@ts/components/utils/SvgVue.vue';
import Chart from 'chart.js/auto';
import { color } from 'chart.js/helpers';
import {} from 'chart.js/auto';
import {} from '@ts/common/chart';
export default /*@__PURE__*/ _defineComponent({
    __name: 'ChartReference',
    props: {
        data: { default: undefined },
        selectable: { type: Boolean, default: false },
        help: { type: Boolean, default: false },
        header: { type: Boolean, default: true }
    },
    setup(__props) {
        const props = __props;
        const showHelp = ref(false);
        const showPlaceholder = ref(true);
        const chart = ref(null);
        const containerRef = ref(null);
        let chartObj = undefined;
        const referenceGroupIdStr = ref(props.data?.reference_group_id !== undefined ? String(props.data.reference_group_id) : '');
        const emptyReferenceOption = { label: ___({ en: 'Select a reference group', de: 'Wähle eine Referenzgruppe' }), disabled: true, hidden: true };
        const referenceGroupOptions = ref(props.data ? computeReferenceOptions(props.data) : {});
        const referenceGroupId = computed(() => {
            return referenceGroupIdStr.value !== '' ? parseInt(referenceGroupIdStr.value) : undefined;
        });
        // Create the chart only once its container has a real (non-zero) size — see useDeferredChart.
        useDeferredChart(containerRef, {
            create: () => {
                if (props.data !== undefined)
                    drawChart(props.data);
            },
            getChart: () => chartObj,
        });
        watch(() => props.data, (newValue, oldValue) => {
            if (newValue !== undefined) {
                referenceGroupOptions.value = computeReferenceOptions(newValue);
                referenceGroupIdStr.value = String(newValue.reference_group_id);
            }
        }, { deep: true });
        watch(() => referenceGroupId.value, (newValue, oldValue) => {
            if (props.data !== undefined)
                drawChart(props.data);
        });
        function computeReferenceOptions(data) {
            const opts = {};
            const keys = Object.keys(data.reference_groups);
            for (const key of keys) {
                const group = data.reference_groups[key];
                opts[key] = { label: group.name };
            }
            return opts;
        }
        function powerAtEnergyCarboPerHour(data, energyCarboPerHour) {
            let i = closest(energyCarboPerHour, data.chart_metabolism.energy_carbo_per_hour);
            if (i > 0 && i < data.chart_metabolism.power.length - 1)
                return data.chart_metabolism.power[i];
            else
                return undefined;
        }
        function radarValueToText(value) {
            if (value > 80) {
                return ___({
                    en: 'very good',
                    de: 'sehr gut',
                });
            }
            else if (value > 60) {
                return ___({
                    en: 'good',
                    de: 'gut',
                });
            }
            else if (value > 35) {
                return ___({
                    en: 'to improve',
                    de: 'zu verbessern',
                });
            }
            else {
                return ___({
                    en: 'weak',
                    de: 'schwach',
                });
            }
        }
        function dataForChart(data) {
            if (referenceGroupId.value === undefined) {
                console.warn('Reference group not set');
                return undefined;
            }
            // console.log('Reference group set:', referenceGroupId.value);
            const referenceGroup = data.reference_groups[referenceGroupId.value];
            const kcalMatch = data.weight * 0.7 * 4.1;
            let chmatchForRef = powerAtEnergyCarboPerHour(data, kcalMatch);
            if (chmatchForRef === undefined) {
                console.warn('chmatchForRef not set');
                return undefined;
            }
            let fatmaxrelForRef = data.fatmax_rel;
            let vo2maxrelForRef = data.vo2max_rel;
            let vlamaxForRef = data.vlamax;
            let thresholdabsForRef = data.threshold_abs;
            let thresholdrelForRef = data.threshold_rel;
            let bmiForRef = data.bmi;
            let bodyfatForRef = data.bodyfat;
            const minFatmaxrelRef = referenceGroup.minFatmaxrel; // 53.5;
            const maxFatmaxrelRef = referenceGroup.maxFatmaxrel; //70.5;
            fatmaxrelForRef = (100 * (fatmaxrelForRef - minFatmaxrelRef)) / (maxFatmaxrelRef - minFatmaxrelRef);
            const minVo2maxRef = referenceGroup.minVo2max; // 53.5;
            const maxVo2maxRef = referenceGroup.maxVo2max; //70.5;
            vo2maxrelForRef = (100 * (vo2maxrelForRef - minVo2maxRef)) / (maxVo2maxRef - minVo2maxRef);
            const minVLamaxRef = referenceGroup.minVLamax; //0.7;
            const maxVLamaxRef = referenceGroup.maxVLamax; //0.2;
            vlamaxForRef = (100 * (minVLamaxRef - vlamaxForRef)) / (minVLamaxRef - maxVLamaxRef);
            const minThresholdabsRef = referenceGroup.minThresholdabs; //205;
            const maxThresholdabsRef = referenceGroup.maxThresholdabs; //445;
            thresholdabsForRef = (100 * (thresholdabsForRef - minThresholdabsRef)) / (maxThresholdabsRef - minThresholdabsRef);
            const minThresholdrelRef = referenceGroup.minThresholdrel; // 2.85;
            const maxThresholdrelRef = referenceGroup.maxThresholdrel; //5.5;
            thresholdrelForRef = (100 * (thresholdrelForRef - minThresholdrelRef)) / (maxThresholdrelRef - minThresholdrelRef);
            // if (hasBodyfat(data)) {
            if (data.bodyfat !== null) {
                const minBodyfatRef = referenceGroup.minBodyfat; //30;
                const maxBodyfatRef = referenceGroup.maxBodyfat; //3;
                bodyfatForRef = (100 * (minBodyfatRef - data.bodyfat)) / (minBodyfatRef - maxBodyfatRef);
            }
            else {
                const minBmiRef = referenceGroup.minBmi; //30;
                const maxBmiRef = referenceGroup.maxBmi; //12;
                bmiForRef = (100 * (minBmiRef - bmiForRef)) / (minBmiRef - maxBmiRef);
            }
            const minChmatchRef = referenceGroup.minChmatch; //150;
            const maxChmatchRef = referenceGroup.maxChmatch; //250;
            chmatchForRef = (100 * (chmatchForRef - minChmatchRef)) / (maxChmatchRef - minChmatchRef);
            fatmaxrelForRef = Math.min(fatmaxrelForRef, 95.0);
            fatmaxrelForRef = Math.max(fatmaxrelForRef, 10.0);
            vo2maxrelForRef = Math.min(vo2maxrelForRef, 95.0);
            vo2maxrelForRef = Math.max(vo2maxrelForRef, 10.0);
            vlamaxForRef = Math.min(vlamaxForRef, 95.0);
            vlamaxForRef = Math.max(vlamaxForRef, 10.0);
            thresholdabsForRef = Math.min(thresholdabsForRef, 95.0);
            thresholdabsForRef = Math.max(thresholdabsForRef, 10.0);
            thresholdrelForRef = Math.min(thresholdrelForRef, 95.0);
            thresholdrelForRef = Math.max(thresholdrelForRef, 10.0);
            chmatchForRef = Math.min(chmatchForRef, 95.0);
            chmatchForRef = Math.max(chmatchForRef, 10.0);
            bmiForRef = Math.min(bmiForRef, 95.0);
            bmiForRef = Math.max(bmiForRef, 10.0);
            bodyfatForRef = bodyfatForRef ? Math.min(bodyfatForRef, 95.0) : null;
            bodyfatForRef = bodyfatForRef ? Math.max(bodyfatForRef, 10.0) : null;
            return [vo2maxrelForRef, vlamaxForRef, thresholdabsForRef, thresholdrelForRef, fatmaxrelForRef, (bodyfatForRef !== null) ? bodyfatForRef : bmiForRef];
        }
        function rawDataForChart(data) {
            return [data.vo2max_rel, data.vlamax, data.threshold_abs, data.threshold_rel, data.fatmax_rel, (data.bodyfat !== null) ? data.bodyfat : data.bmi];
        }
        function drawChart(data) {
            if (chart.value === null)
                return;
            const dataFC = dataForChart(data);
            const rawDataFC = rawDataForChart(data);
            if (dataFC === undefined) {
                return;
            }
            const chartData = {
                labels: [
                    'VO2max',
                    'VLamax',
                    ___({ en: 'abs. Threshold', de: 'abs. ANS' }),
                    ___({ en: 'rel. Threshold', de: 'rel. ANS' }),
                    ___({ en: 'Fatmax', de: 'Fatmax' }),
                    (data.bodyfat ?? false) ? ___({ en: 'BFP', de: 'KFA' }) : 'BMI',
                ],
                datasets: [
                    {
                        backgroundColor: color('#000000').alpha(0.1).rgbString(),
                        borderColor: '#000000',
                        pointBackgroundColor: '#000000',
                        label: ___({
                            en: 'Analysis',
                            de: 'Analyse',
                        }),
                        data: dataFC,
                        rawData: rawDataFC,
                        // parsing: false,
                    },
                    {
                        backgroundColor: '#636362',
                        borderColor: '#636362',
                        pointBackgroundColor: '#636362',
                        label: radarValueToText(24),
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 0,
                        showLine: false,
                        borderWidth: 0,
                        data: [35, 35, 35, 35, 35, 35],
                        // parsing: false,
                    },
                    {
                        backgroundColor: '#ffffff',
                        borderColor: '#ffffff',
                        pointBackgroundColor: '#ffffff',
                        label: radarValueToText(49),
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 0,
                        showLine: false,
                        borderWidth: 0,
                        data: [60, 60, 60, 60, 60, 60],
                        // parsing: false,
                    },
                    {
                        backgroundColor: '#2CB7C1',
                        borderColor: '#2CB7C1',
                        pointBackgroundColor: '#2CB7C1',
                        label: radarValueToText(74),
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 0,
                        showLine: false,
                        borderWidth: 0,
                        data: [80, 80, 80, 80, 80, 80],
                        // parsing: false,
                    },
                    {
                        backgroundColor: '#CDB663',
                        borderColor: '#CDB663',
                        pointBackgroundColor: '#CDB663',
                        label: radarValueToText(99),
                        pointRadius: 0,
                        pointHoverRadius: 0,
                        pointHitRadius: 0,
                        showLine: false,
                        borderWidth: 0,
                        data: [100, 100, 100, 100, 100, 100],
                        // parsing: false,
                    },
                ],
            };
            const chartOptions = {
                maintainAspectRatio: false,
                responsive: true,
                animation: false,
                // responsiveAnimationDuration: 0,
                // title: {
                //   display: false,
                // },
                scales: {
                    r: {
                        max: 100,
                        min: 0,
                        ticks: {
                            display: false,
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'right',
                        onClick: () => {
                            return;
                        },
                    },
                    tooltip: {
                        displayColors: false,
                        callbacks: {
                            title: (context) => {
                                return context[0].label;
                            },
                            label: (context) => {
                                let decNum = 1;
                                let unitStr = '';
                                switch (context.label) {
                                    case 'VO2max':
                                        decNum = 1;
                                        unitStr = 'ml/min/kg';
                                        break;
                                    case 'VLamax':
                                        decNum = 2;
                                        unitStr = 'mmol/l/s';
                                        break;
                                    case 'abs. Threshold':
                                    case 'abs. ANS':
                                        decNum = 1;
                                        unitStr = 'W';
                                        break;
                                    case 'rel. Threshold':
                                    case 'rel. ANS':
                                        decNum = 2;
                                        unitStr = 'W/kg';
                                        break;
                                    case 'Fatmax':
                                        decNum = 2;
                                        unitStr = 'W/kg';
                                        break;
                                    case 'BFP':
                                    case 'KFA':
                                        decNum = 1;
                                        unitStr = '%';
                                        break;
                                    case 'BMI':
                                        decNum = 1;
                                        unitStr = '';
                                        break;
                                    default:
                                        decNum = 1;
                                        unitStr = '';
                                }
                                return toFixedNumberStr((context.dataset.rawData)[context.dataIndex], decNum) + ' ' + unitStr;
                            },
                        },
                    },
                },
            };
            if (chartObj === undefined) {
                chartObj = new Chart(chart.value, {
                    type: 'radar',
                    data: chartData,
                    options: chartOptions,
                });
                showPlaceholder.value = false;
            }
            else {
                chartObj.data = chartData;
                chartObj.options = chartOptions;
                chartObj.update();
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                (__props.header)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                        _createElementVNode("div", _hoisted_3, [
                            _createElementVNode("div", _hoisted_4, _toDisplayString(_unref(___)({ en: 'Reference', de: 'Referenzdaten' })), 1 /* TEXT */),
                            (__props.help)
                                ? (_openBlock(), _createElementBlock("button", {
                                    key: 0,
                                    onClick: _cache[1] || (_cache[1] = ($event) => (showHelp.value = !showHelp.value)),
                                    type: "button",
                                    class: "btn text-gray-350 dark:text-gray-450 hover:text-primary-500",
                                    "aria-label": _unref(___)({ en: 'Toggle help', de: 'Hilfe ein- und ausblenden' }),
                                    title: _unref(___)({ en: 'Help', de: 'Hilfe' })
                                }, [
                                    _cache[0] || (_setBlockTracking(-1, true),
                                        (_cache[0] = _createVNode(SvgVue, {
                                            class: "w-4 h-4",
                                            icon: "lucide/circle-question-mark"
                                        })).cacheIndex = 0,
                                        _setBlockTracking(1),
                                        _cache[0])
                                ], 8 /* PROPS */, _hoisted_5))
                                : _createCommentVNode("v-if", true)
                        ]),
                        (__props.help)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: _normalizeClass(["collapsible", showHelp.value ? 'expanded' : ''])
                            }, [
                                _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(___)({
                                    en: 'This chart gives you an overview of the most important metrics and compares them to a selected reference group.',
                                    de: 'Diese Grafik gibt dir eine Übersicht der wichtigsten Kennzahlen und vergleicht sie mit einer ausgewählten Referenzgruppe.',
                                })), 1 /* TEXT */)
                            ], 2 /* CLASS */))
                            : _createCommentVNode("v-if", true)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("div", {
                        class: "absolute inset-0",
                        ref_key: "containerRef",
                        ref: containerRef
                    }, [
                        _createElementVNode("canvas", {
                            class: "w-full h-full",
                            ref_key: "chart",
                            ref: chart
                        }, null, 512 /* NEED_PATCH */)
                    ], 512 /* NEED_PATCH */),
                    (showPlaceholder.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                            _createElementVNode("div", null, [
                                _cache[2] || (_setBlockTracking(-1, true),
                                    (_cache[2] = _createVNode(SvgVue, {
                                        class: "w-4 h-4",
                                        icon: "lucide/chart-line"
                                    })).cacheIndex = 2,
                                    _setBlockTracking(1),
                                    _cache[2]),
                                _createTextVNode(" " + _toDisplayString(_unref(___)({ en: 'loading...', de: 'lädt...' })), 1 /* TEXT */)
                            ])
                        ]))
                        : _createCommentVNode("v-if", true)
                ]),
                (__props.selectable)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createVNode(LabeledSelect, {
                            title: _unref(___)({ en: 'Reference group', de: 'Referenzgruppe' }),
                            help: _unref(___)({ en: 'Reference group', de: 'Referenzgruppe' }),
                            value: referenceGroupIdStr.value,
                            "onUpdate:value": _cache[3] || (_cache[3] = ($event) => ((referenceGroupIdStr).value = $event)),
                            "empty-option": emptyReferenceOption,
                            options: referenceGroupOptions.value,
                            disabled: false
                        }, null, 8 /* PROPS */, ["title", "help", "value", "options"])
                    ]))
                    : _createCommentVNode("v-if", true)
            ]));
        };
    }
});
